import type { Mercury } from '@repo/types/Models';

const ANNOUNCEMENTS_KEY = 'announcements';
const DISMISSED_ANNOUNCEMENTS_KEY = 'dismissedAnnouncements';

/**
 * Get all announcements from localStorage
 */
export function getAnnouncements(): Mercury.Models.Announcement[] {
  try {
    const data = localStorage.getItem(ANNOUNCEMENTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading announcements from localStorage:', error);
    return [];
  }
}

/**
 * Get a single announcement by ID
 */
export function getAnnouncementById(id: string): Mercury.Models.Announcement | null {
  const announcements = getAnnouncements();
  return announcements.find((a) => a.id === id) || null;
}

/**
 * Save all announcements to localStorage
 */
export function saveAnnouncements(announcements: Mercury.Models.Announcement[]): void {
  try {
    localStorage.setItem(ANNOUNCEMENTS_KEY, JSON.stringify(announcements));
  } catch (error) {
    console.error('Error saving announcements to localStorage:', error);
  }
}

/**
 * Create a new announcement
 */
export function createAnnouncement(
  announcement: Omit<Mercury.Models.Announcement, 'id' | 'createdAt' | 'updatedAt'>
): Mercury.Models.Announcement {
  const announcements = getAnnouncements();
  const now = new Date().toISOString();

  const newAnnouncement: Mercury.Models.Announcement = {
    ...announcement,
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
  };

  announcements.push(newAnnouncement);
  saveAnnouncements(announcements);

  return newAnnouncement;
}

/**
 * Update an existing announcement
 */
export function updateAnnouncement(
  id: string,
  updates: Partial<Omit<Mercury.Models.Announcement, 'id' | 'createdAt' | 'updatedAt'>>
): Mercury.Models.Announcement | null {
  const announcements = getAnnouncements();
  const index = announcements.findIndex((a) => a.id === id);

  if (index === -1) {
    return null;
  }

  const updatedAnnouncement: Mercury.Models.Announcement = {
    ...announcements[index],
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  announcements[index] = updatedAnnouncement;
  saveAnnouncements(announcements);

  return updatedAnnouncement;
}

/**
 * Delete an announcement
 */
export function deleteAnnouncement(id: string): boolean {
  const announcements = getAnnouncements();
  const filtered = announcements.filter((a) => a.id !== id);

  if (filtered.length === announcements.length) {
    return false; // Nothing was deleted
  }

  saveAnnouncements(filtered);
  return true;
}

/**
 * Get active announcements (filtered by active status and date range)
 */
export function getActiveAnnouncements(): Mercury.Models.Announcement[] {
  const announcements = getAnnouncements();
  const now = new Date();

  return announcements.filter((announcement) => {
    // Must be active
    if (!announcement.isActive) {
      return false;
    }

    // Check start date
    if (announcement.startDate) {
      const startDate = new Date(announcement.startDate);
      if (now < startDate) {
        return false;
      }
    }

    // Check end date
    if (announcement.endDate) {
      const endDate = new Date(announcement.endDate);
      if (now > endDate) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get dismissed announcement IDs for a user
 */
export function getDismissedAnnouncementIds(userId: string): string[] {
  try {
    const data = localStorage.getItem(DISMISSED_ANNOUNCEMENTS_KEY);
    const dismissed: Record<string, string[]> = data ? JSON.parse(data) : {};
    return dismissed[userId] || [];
  } catch (error) {
    console.error('Error reading dismissed announcements from localStorage:', error);
    return [];
  }
}

/**
 * Mark an announcement as dismissed for a user
 */
export function dismissAnnouncement(userId: string, announcementId: string): void {
  try {
    const data = localStorage.getItem(DISMISSED_ANNOUNCEMENTS_KEY);
    const dismissed: Record<string, string[]> = data ? JSON.parse(data) : {};

    if (!dismissed[userId]) {
      dismissed[userId] = [];
    }

    if (!dismissed[userId].includes(announcementId)) {
      dismissed[userId].push(announcementId);
      localStorage.setItem(DISMISSED_ANNOUNCEMENTS_KEY, JSON.stringify(dismissed));
    }
  } catch (error) {
    console.error('Error dismissing announcement:', error);
  }
}

/**
 * Get active announcements that haven't been dismissed by the user
 */
export function getVisibleAnnouncements(userId: string): Mercury.Models.Announcement[] {
  const activeAnnouncements = getActiveAnnouncements();
  const dismissedIds = getDismissedAnnouncementIds(userId);

  return activeAnnouncements.filter((announcement) => !dismissedIds.includes(announcement.id));
}
